import React, { useCallback } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { SalesPlanningTypes, salesPlanning } from 'api';
import COUNTRIES from 'constants/countries';
import { OpenInNewOutlined } from '@mui/icons-material';
import { HEADER_HEIGHT } from 'theme/constants';
import { appLinks } from 'routes/links';
import {
  formatCurrency,
  formatNumber,
} from '../../../../../helpers/formatters';

interface Props {
  inline?: boolean;
  planning: SalesPlanningTypes.ItemData;
}

interface Metric {
  key: keyof SalesPlanningTypes.ItemData;
  label: string;
  value: React.ReactNode;
}

const PreviousSalesPlannings = (props: Props): JSX.Element | null => {
  const { planning, inline } = props;

  const { data, error } = useQuery([salesPlanning.endpoints.getList], () =>
    salesPlanning
      .getList({
        sort_by: 'start_date',
        sort_desc: true,
        limit: 5,
        filters: {
          start_date: { $lte: planning.start_date },
          brands: planning.brands,
          country_team: planning.country_team,
        },
      })
      .then((res) => res.data.filter((r) => r._id.$oid !== planning._id.$oid)),
  );

  const planningMetrics = useCallback((c: SalesPlanningTypes.ItemData) => {
    const metrics: Metric[] = [
      {
        key: 'deals_nb',
        label: 'Deals',
        value: c.deals_nb,
      },
      {
        key: 'influencers_nb',
        label: 'Influencers',
        value: c.influencers_nb,
      },
      {
        key: 'reach_total',
        label: 'Reach',
        value: c.reach_total,
      },
      {
        key: 'budget_actual',
        label: 'Budget actual',
        value: formatCurrency(c.budget_actual),
      },
      {
        key: 'revenue_actual',
        label: 'Revenue actual',
        value: formatCurrency(c.revenue_actual),
      },
      {
        key: 'roi_actual',
        label: 'ROI actual',
        value: formatNumber(c.roi_actual),
      },
    ];

    return metrics;
  }, []);

  return (
    <Box
      sx={{
        overflowY: 'auto',
        height: inline ? '100%' : `calc(100vh - ${HEADER_HEIGHT}px - 64px)`,
      }}
    >
      <>
        {error && (
          <Typography variant="subtitle2">
            Failed to load previous sales plannings
          </Typography>
        )}
        {!data &&
          !error &&
          Array.from(new Array(5))
            .map((_, i) => i)
            .map((item) => (
              <Card variant="outlined" key={item} sx={{ mb: 2 }}>
                <CardHeader
                  avatar={
                    <Skeleton
                      variant="circular"
                      sx={{
                        width: 25,
                        height: 25,
                        borderRadius: (t) => `${t.shape.borderRadius}px`,
                      }}
                    />
                  }
                  title={<Skeleton variant="text" sx={{ width: '70%' }} />}
                />
                <CardContent>
                  <Skeleton variant="text" sx={{ width: '80%', height: 50 }} />
                </CardContent>
                <CardActions>
                  <Skeleton variant="text" sx={{ width: '30%', height: 30 }} />
                </CardActions>
              </Card>
            ))}
        {data && data.length > 0 ? (
          data.map((c) => (
            <Card variant="outlined" key={c.sales_planning_id} sx={{ mb: 2 }}>
              <CardHeader
                avatar={
                  <Avatar
                    src={COUNTRIES.LIST[c.country_team].image}
                    sx={{
                      width: 25,
                      height: 25,
                      borderRadius: (t) => `${t.shape.borderRadius}px`,
                    }}
                  />
                }
                title={c.name}
                titleTypographyProps={{ variant: 'subtitle2' }}
              />
              <CardContent>
                {planningMetrics(c).map((m) => (
                  <Stack
                    direction="row"
                    columnGap={2}
                    key={m.key}
                    flexWrap="wrap"
                  >
                    <Typography variant="body2" flexGrow={1}>
                      {m.label}
                    </Typography>
                    <Typography color="text.secondary">
                      {m.value || '-'}
                    </Typography>
                  </Stack>
                ))}
              </CardContent>
              <CardActions>
                <Button
                  size="small"
                  href={appLinks.sales.details.as(c.sales_planning_id)}
                  target="_blank"
                  endIcon={<OpenInNewOutlined />}
                >
                  Open sales planning
                </Button>
              </CardActions>
            </Card>
          ))
        ) : (
          <Typography variant="subtitle2">
            No previous sales plannings
          </Typography>
        )}
      </>
    </Box>
  );
};

export default PreviousSalesPlannings;
